/**
 * Mi cuenta.
 *
 * Sólo el cambio de contraseña, de momento. No lleva permiso: cualquiera
 * puede cambiar la suya, y exigir uno dejaría fuera justo a quien menos
 * permisos tiene, que es quien más probable es que necesite hacerlo.
 */

import { useState } from 'react';
import { useSession } from '../session/session-context.js';
import { useForm } from '../forms/use-form.js';
import { FormSection, TextField } from '../forms/fields.js';
import { minLength, required, type FieldRules } from '../forms/form-state.js';

const RULES: FieldRules = {
  currentPassword: [required('La contraseña actual')],
  // La política real la impone el servidor (NIST SP 800-63B). Aquí sólo se
  // adelanta el mínimo para no hacer ir y volver por algo evidente.
  newPassword: [required('La contraseña nueva'), minLength(12)],
  repeatPassword: [required('La repetición')],
};

export function AccountScreen(): React.ReactNode {
  const { user, logout } = useSession();
  const [done, setDone] = useState(false);
  const [mismatch, setMismatch] = useState<string | null>(null);

  const form = useForm<void>({
    mode: 'create',
    rules: RULES,
    initial: { currentPassword: '', newPassword: '', repeatPassword: '' },
    onSubmit: async (body, client) => {
      const values = body as Record<string, string>;
      return client.request<void>('/users/me/password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: values['currentPassword'],
          newPassword: values['newPassword'],
        }),
      });
    },
    onSaved: () => setDone(true),
  });

  if (done) {
    return (
      <section className="screen">
        <h2 className="screen__title">Contraseña cambiada</h2>
        <p className="screen__note">
          Se han cerrado todas las sesiones, incluida esta. Vuelva a entrar con la contraseña nueva.
        </p>
        <button className="button button--primary" type="button" onClick={() => void logout()}>
          Ir a iniciar sesión
        </button>
      </section>
    );
  }

  return (
    <section className="screen">
      <h2 className="screen__title">Mi cuenta</h2>
      <p className="screen__note">
        {(user?.roles ?? []).join(', ') || 'Sin roles asignados'}
      </p>

      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (form.values['newPassword'] !== form.values['repeatPassword']) {
            setMismatch('Las dos contraseñas nuevas no coinciden.');
            return;
          }
          setMismatch(null);
          void form.submit();
        }}
      >
        {mismatch !== null && (
          <p className="screen__error" role="alert">
            {mismatch}
          </p>
        )}
        {form.formError !== null && (
          <p className="screen__error" role="alert">
            {form.formError}
          </p>
        )}

        <FormSection title="Cambiar la contraseña">
          <TextField
            label="Contraseña actual"
            type="password"
            value={String(form.values['currentPassword'] ?? '')}
            onChange={(v) => form.set('currentPassword', v)}
            onBlur={() => form.blur('currentPassword')}
            error={form.errors['currentPassword']}
            required
          />
          <TextField
            label="Contraseña nueva"
            type="password"
            value={String(form.values['newPassword'] ?? '')}
            onChange={(v) => form.set('newPassword', v)}
            onBlur={() => form.blur('newPassword')}
            error={form.errors['newPassword']}
            help="Mínimo 12 caracteres. Larga y fácil de recordar es mejor que corta y retorcida."
            required
          />
          <TextField
            label="Repita la nueva"
            type="password"
            value={String(form.values['repeatPassword'] ?? '')}
            onChange={(v) => form.set('repeatPassword', v)}
            onBlur={() => form.blur('repeatPassword')}
            error={form.errors['repeatPassword']}
            required
          />
        </FormSection>

        <button className="button button--primary" type="submit" disabled={form.saving}>
          {form.saving ? 'Cambiando…' : 'Cambiar contraseña'}
        </button>
      </form>
    </section>
  );
}
